import { FC } from 'react';
import { v4 as uuid4 } from 'uuid';
import Typography from '@components/typography';
import classnames from 'classnames';
import ReviewStarFilled from '@components/reviewstarfilledicon';
import ReviewStarhalfFilled from '@components/reviewstarhalficon';
import ReviewStarUnfilled from '@components/reviewstarunfilledicon';

import '@styles/tag.scss';
import '@styles/reviewtag.scss';
import CarwowLogo from '@components/carwowlogo';
import { ReviewTagProps } from 'interface';

const ReviewTag: FC<ReviewTagProps> = ({
  classNames,
  rating,
  'data-cy': dataCy,
}) => {
  // Error checking before we render the component
  const checkRating: number = Number(rating);
  if (isNaN(checkRating)) {
    console.error(
      `Unexpected review value type. Value type should be a number. Value is type '${typeof rating}'.`
    );
    return <></>;
  }
  if (Number(rating) < 0 || Number(rating) > 10) {
    console.error(
      `Unexpected review value. Value should be between 0-10. Values is '${rating}'.`
    );
    return <></>;
  }

  /**
   * A function to take the review score and convert it to the correct amount of stars
   *
   * @param value The cars review rating as a number string of 0 to 10
   * @returns An array of Star icons
   */
  const getStars = (value: string): JSX.Element => {
    const numberOfStars: number = 5;
    const valueToStars: number = Number(value) / 2;

    const ratingCalc: number[] = String(valueToStars)
      .split('.')
      .map((num) => Number(num));

    const wholeStars: number = ratingCalc[0];
    const partialStars: number = ratingCalc[1];

    // Add whole stars first
    let reviewRating: string[] = Array(wholeStars).fill('full', 0);

    // Then add partials for the odd numbers, if any
    if (partialStars) {
      reviewRating.push('half');
    }

    // Then fill the rest of the score with unfilled stars
    if (reviewRating.length <= numberOfStars) {
      const filler: number = numberOfStars - reviewRating.length;

      for (let i = 0; i < filler; i++) {
        reviewRating.push('empty');
      }
    }

    return (
      <ul
        className="reviewtag__stars-container"
        data-cy={`${dataCy}-stars`}
        aria-hidden="true"
      >
        {reviewRating.map((val) => {
          switch (val) {
            case 'full':
              return (
                <li key={uuid4()} className="reviewtag__star">
                  <ReviewStarFilled />
                </li>
              );
            case 'half':
              return (
                <li key={uuid4()} className="reviewtag__star">
                  <ReviewStarhalfFilled />
                </li>
              );
            default:
              return (
                <li key={uuid4()} className="reviewtag__star">
                  <ReviewStarUnfilled />
                </li>
              );
          }
        })}
      </ul>
    );
  };

  return (
    <div
      data-cy={`${dataCy}-tag`}
      data-testid={`${dataCy}-tag`}
      className={classnames(classNames, 'tag__review')}
      title={`Car wow review score: ${rating} out of 10`}
      role="img"
    >
      <CarwowLogo
        className="reviewtag__logo"
        data-cy={`${dataCy}-logo`}
        colour="white"
        width="65"
        height="16"
        aria-hidden={true}
      />
      {getStars(rating as string)}
      <Typography
        data-cy={`${dataCy}-rating`}
        variant="caption"
        size="lg"
        weight="bold"
        colour="alternative"
        aria-hidden={true}
      >
        {rating}
      </Typography>
    </div>
  );
};

export default ReviewTag;
